import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import RegistrationForm from "./RegistrationForm";

import formatFormValues from "../helpers/formatFormValues";

import Swal from "sweetalert2";

function ParticipantList({
  qty,
  formValues,
  setFormValues,
  isRegistrationFormValid,
  setIsRegistrationFormValid,
  genderOptions,
  jerseySizeOptions,
  formErrors,
  gloveSizeOptions,
  swingSpeedOptions,
  preferenceOptions,
  participantCategoryOptions,
}) {
  const { t } = useTranslation();
  const participantListRef = useRef(null);

  const [openAccordion, setOpenAccordion] = useState(0);

  const [selectedJerseySizes, setSelectedJerseySizes] = useState({}); // { 0: "s-id", 1: "xl-id" }
  const [selectedGloveSizes, setSelectedGloveSizes] = useState({}); // { 0: "18-id", 1: "23-id" }

  const handleSizeChange = (index, sizeId, keyName) => {
    if (keyName === "jersey") {
      setSelectedJerseySizes((prev) => ({ ...prev, [index]: sizeId }));
    } else {
      // consider as glove size input
      setSelectedGloveSizes((prev) => {
        const updated = { ...prev };
        if (sizeId === "") {
          delete updated[index];
        } else {
          updated[index] = sizeId;
        }
        return updated;
      });
    }
  };

  const getAvailableOptions = (currentIndex, sizeOptions, keyName, gender) => {
    let selectedSizes =
      keyName === "jersey" ? selectedJerseySizes : selectedGloveSizes;
    let finalSizeOptions = sizeOptions;

    if (gender) {
      if (gender === "Male") {
        finalSizeOptions = finalSizeOptions.filter(
          (sizeOpt) => Number(sizeOpt.name) > 21,
        );
      } else {
        // for female
        finalSizeOptions = finalSizeOptions.filter(
          (sizeOpt) => Number(sizeOpt.name) < 22,
        );
      }
    }
    return finalSizeOptions.filter((size) => {
      const timesSelected = Object.entries(selectedSizes)
        .filter(([idx]) => Number(idx) !== currentIndex)
        .filter(([, sizeId]) => Number(sizeId) === size.value).length;

      return size.stock - timesSelected > 0;
    });
  };

  useEffect(() => {
    // Add new form values when qty increased
    if (qty && qty > 0) {
      let newFormValues = formatFormValues(
        [
          "customer-name",
          "customer-email",
          "phone-number",
          "customer-phcode",
          "swing-speed",
          "preference",
          "notes",
          "gender",
          "jersey-size",
          "glove-size",
          "participant-category",
          "participant-category-other",
        ],
        formValues,
        qty,
      );
      setFormValues(newFormValues);
    }
  }, [qty]);

  useEffect(() => {
    // Re-focus to form if there are invalid inputs
    if (!isRegistrationFormValid) {
      participantListRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
      Swal.fire({
        title: "info",
        text: "Please recheck all required input fields",
        icon: "info",
        position: "top-right",
        returnFocus: false,
      });
    }
  }, [isRegistrationFormValid]);

  useEffect(() => {
    if (qty && qty > 0) {
      setOpenAccordion(qty - 1);
    }
  }, [qty]);

  const handleFormInput = (e) => {
    setIsRegistrationFormValid(true);
    const value = e && e.target && e.target.value;
    const name = e && e.target && e.target.name;
    let newFormValues = Object.assign({}, formValues);
    newFormValues[name] = value;

    // gender changed = empty / reset glove size value (if any)
    if (name && name.startsWith("gender-")) {
      const idx = name.split("-")[1]; // get input index
      newFormValues[`glove-size-${idx}`] = "";
    }

    setFormValues(newFormValues);
  };

  const renderParticipantList = () => {
    let component = [];
    if (qty && parseInt(qty) > 0) {
      for (let i = 0; i < qty; i++) {
        component.push(
          <div className="mb-3" key={i}>
            <div className="flex justify-between px-4 py-2 border-2 rounded-md shadow-md border-slate-200">
              <div className="text-base font-bold md:text-lg">
                {t("participantPlaceholder")} {i + 1}
              </div>
              <button
                onClick={() => setOpenAccordion(i)}
                className="text-xs md:text-sm"
              >
                {t("viewDetails")}
              </button>
            </div>
            <div
              className={`border-2 border-slate-200 rounded-md p-5 bg-slate-100 ${i == openAccordion ? null : "hidden"}`}
            >
              <RegistrationForm
                index={i}
                handleFormInput={handleFormInput}
                formValues={formValues}
                isRegistrationFormValid={isRegistrationFormValid}
                genderOptions={genderOptions}
                formErrors={formErrors}
                jerseySizeOptions={getAvailableOptions(
                  i,
                  jerseySizeOptions,
                  "jersey",
                )}
                onSizeChange={(sizeId, keyName) =>
                  handleSizeChange(i, sizeId, keyName)
                }
                gloveSizeOptions={getAvailableOptions(
                  i,
                  gloveSizeOptions,
                  "glove",
                  formValues[`gender-${i}`],
                )}
                swingSpeedOptions={swingSpeedOptions}
                preferenceOptions={preferenceOptions}
                participantCategoryOptions={participantCategoryOptions}
              />
            </div>
          </div>,
        );
      }
    }
    return component;
  };

  return <div ref={participantListRef}>{renderParticipantList()}</div>;
}

export default ParticipantList;
